import { ProjectFile, FileTreeNode } from '@/types';

// Get Monaco editor language from file extension
export const getLanguageFromPath = (path: string): string => {
  const ext = path.split('.').pop()?.toLowerCase();
  
  const languageMap: { [key: string]: string } = {
    'js': 'javascript',
    'jsx': 'javascript',
    'ts': 'typescript',
    'tsx': 'typescript',
    'html': 'html',
    'htm': 'html',
    'css': 'css',
    'scss': 'scss',
    'sass': 'sass',
    'less': 'less',
    'php': 'php',
    'json': 'json',
    'xml': 'xml',
    'md': 'markdown',
    'markdown': 'markdown',
    'txt': 'plaintext',
    'py': 'python',
    'java': 'java',
    'c': 'c',
    'cpp': 'cpp',
    'cs': 'csharp',
    'go': 'go',
    'rs': 'rust',
    'rb': 'ruby',
    'sh': 'shell',
    'bash': 'shell',
    'sql': 'sql',
    'yml': 'yaml',
    'yaml': 'yaml',
  };

  return languageMap[ext || ''] || 'plaintext';
};

// Get file icon based on file type
export const getFileIcon = (name: string, isFolder: boolean = false): string => {
  if (isFolder) return '📁';
  
  const ext = name.split('.').pop()?.toLowerCase();
  
  const iconMap: { [key: string]: string } = {
    'js': '📄',
    'jsx': '⚛️',
    'ts': '📘',
    'tsx': '⚛️',
    'html': '🌐',
    'htm': '🌐',
    'css': '🎨',
    'scss': '🎨',
    'sass': '🎨',
    'less': '🎨',
    'php': '🐘',
    'json': '📋',
    'xml': '📄',
    'md': '📝',
    'markdown': '📝',
    'txt': '📄',
    'py': '🐍',
    'java': '☕',
    'c': '📄',
    'cpp': '📄',
    'cs': '📄',
    'go': '🐹',
    'rs': '🦀',
    'rb': '💎',
    'sh': '📜',
    'bash': '📜',
    'sql': '🗄️',
    'yml': '⚙️',
    'yaml': '⚙️',
    'png': '🖼️',
    'jpg': '🖼️',
    'jpeg': '🖼️',
    'gif': '🖼️',
    'svg': '🖼️',
    'webp': '🖼️',
    'ico': '🖼️',
    'pdf': '📄',
    'zip': '📦',
    'tar': '📦',
    'gz': '📦',
  };

  return iconMap[ext || ''] || '📄';
};

// Build file tree from flat file list
export const buildFileTree = (files: ProjectFile[]): FileTreeNode[] => {
  const tree: FileTreeNode[] = [];
  const folderMap = new Map<string, FileTreeNode>();

  // Sort files by path
  const sortedFiles = [...files].sort((a, b) => a.path.localeCompare(b.path));

  for (const file of sortedFiles) {
    const pathParts = file.path.split('/');
    let currentPath = '';
    
    // Create folder nodes for each part of the path
    for (let i = 0; i < pathParts.length - 1; i++) {
      const folderName = pathParts[i];
      const parentPath = currentPath;
      currentPath = currentPath ? `${currentPath}/${folderName}` : folderName;
      
      if (!folderMap.has(currentPath)) {
        const folderNode: FileTreeNode = {
          name: folderName,
          path: currentPath,
          type: 'folder',
          children: [],
          expanded: false,
        };
        
        folderMap.set(currentPath, folderNode);
        
        // Add to parent folder or root
        if (parentPath) {
          const parentFolder = folderMap.get(parentPath);
          if (parentFolder) {
            parentFolder.children!.push(folderNode);
          }
        } else {
          tree.push(folderNode);
        }
      }
    }
    
    // Add file node
    const fileName = pathParts[pathParts.length - 1];
    const fileNode: FileTreeNode = {
      name: fileName,
      path: file.path,
      type: 'file',
    };
    
    // Find parent folder or add to root
    const parentPath = pathParts.slice(0, -1).join('/');
    if (parentPath) {
      const parentFolder = folderMap.get(parentPath);
      if (parentFolder) {
        parentFolder.children!.push(fileNode);
      }
    } else {
      tree.push(fileNode);
    }
  }

  // Sort children in each folder (folders first, then files)
  const sortChildren = (nodes: FileTreeNode[]) => {
    nodes.sort((a, b) => {
      if (a.type !== b.type) {
        return a.type === 'folder' ? -1 : 1;
      }
      return a.name.localeCompare(b.name);
    });
    
    nodes.forEach(node => {
      if (node.children) {
        sortChildren(node.children);
      }
    });
  };

  sortChildren(tree);
  return tree;
};

// Find file node in tree
export const findFileNode = (tree: FileTreeNode[], path: string): FileTreeNode | null => {
  for (const node of tree) {
    if (node.path === path) {
      return node;
    }
    if (node.children) {
      const found = findFileNode(node.children, path);
      if (found) return found;
    }
  }
  return null;
};

// Get all expanded folder paths
export const getExpandedPaths = (tree: FileTreeNode[]): string[] => {
  const expanded: string[] = [];
  
  const traverse = (nodes: FileTreeNode[]) => {
    for (const node of nodes) {
      if (node.type === 'folder' && node.expanded) {
        expanded.push(node.path);
      }
      if (node.children) {
        traverse(node.children);
      }
    }
  };
  
  traverse(tree);
  return expanded;
};

// Expand folder path in tree
export const expandPath = (tree: FileTreeNode[], path: string): FileTreeNode[] => {
  const pathParts = path.split('/');
  let currentPath = '';
  
  const newTree = [...tree];
  
  for (let i = 0; i < pathParts.length - 1; i++) {
    currentPath = currentPath ? `${currentPath}/${pathParts[i]}` : pathParts[i];
    const folder = findFileNode(newTree, currentPath);
    if (folder) {
      folder.expanded = true;
    }
  }
  
  return newTree;
};

// Get file extension
export const getFileExtension = (filename: string): string => {
  return filename.split('.').pop()?.toLowerCase() || '';
};

// Check if file is binary
export const isBinaryFile = (filename: string): boolean => {
  const binaryExtensions = [
    'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'ico',
    'pdf', 'zip', 'tar', 'gz', 'rar', '7z',
    'mp3', 'mp4', 'avi', 'mov', 'wav',
    'exe', 'dll', 'so', 'dylib',
  ];
  
  const ext = getFileExtension(filename);
  return binaryExtensions.includes(ext);
};

// Validate file name
export const validateFileName = (name: string): string | null => {
  if (!name.trim()) {
    return 'File name cannot be empty';
  }
  
  if (name.length > 255) {
    return 'File name is too long (max 255 characters)';
  }
  
  // Check for invalid characters
  const invalidChars = /[<>:"|?*\x00-\x1f]/;
  if (invalidChars.test(name)) {
    return 'File name contains invalid characters';
  }
  
  // Check for reserved names (Windows)
  const reservedNames = [
    'CON', 'PRN', 'AUX', 'NUL',
    'COM1', 'COM2', 'COM3', 'COM4', 'COM5', 'COM6', 'COM7', 'COM8', 'COM9',
    'LPT1', 'LPT2', 'LPT3', 'LPT4', 'LPT5', 'LPT6', 'LPT7', 'LPT8', 'LPT9'
  ];
  
  const nameWithoutExt = name.split('.')[0].toUpperCase();
  if (reservedNames.includes(nameWithoutExt)) {
    return 'File name is reserved';
  }
  
  return null;
};

// Format file size
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};